import React, { useState, useEffect } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import SelectDropdown from '../components/SelectDropdown';
import { COMMON_TIMEFRAMES } from '../constants/timeframes';
import { useTaskPolling } from '../hooks/useTaskPolling';
import {
  quantitativeStrategyService,
  type BatchBacktestStatus,
  type BatchOptimizeStatus,
  type StrategyDetailData,
} from '../services/quantitativeStrategyService';

const StrategyDetail: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [strategyCode, setStrategyCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<StrategyDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchBacktesting, setBatchBacktesting] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchBacktestStatus | null>(null);
  const [selectedSymbolKey, setSelectedSymbolKey] = useState<string>('');
  const [batchTimeframe, setBatchTimeframe] = useState<string>('1H');
  // 默认时间范围：最近三个月
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 3 * 30 * 24 * 60 * 60 * 1000);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const defaultDateRange = getDefaultDateRange();

  // 从 localStorage 加载缓存的批量回测配置
  const loadBatchBacktestConfig = (code: string) => {
    try {
      const cached = localStorage.getItem(`batch_backtest_config_${code}`);
      if (cached) {
        const config = JSON.parse(cached);
        return {
          timeframe: config.timeframe || '1H',
          startDate: config.startDate || defaultDateRange.startDate,
          endDate: config.endDate || defaultDateRange.endDate,
        };
      }
    } catch (error) {
      console.error('加载批量回测配置失败:', error);
    }
    return {
      timeframe: '1H',
      startDate: defaultDateRange.startDate,
      endDate: defaultDateRange.endDate,
    };
  };

  const [batchStartDate, setBatchStartDate] = useState<string>(defaultDateRange.startDate);
  const [batchEndDate, setBatchEndDate] = useState<string>(defaultDateRange.endDate);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeTaskId, setOptimizeTaskId] = useState<string | null>(null);
  const [optimizeStatus, setOptimizeStatus] = useState<any>(null);
  const [batchTaskId, setBatchTaskId] = useState<string | null>(null);

  // 批量回测轮询 - 声明式控制，enabled 绑定到 batchBacktesting 状态
  useTaskPolling<BatchBacktestStatus>({
    pollFn: async () => {
      if (!batchTaskId) {
        return { success: false, message: '任务ID不存在' };
      }
      try {
        const data = await quantitativeStrategyService.getBatchBacktestStatus(batchTaskId);
        return { success: true, data };
      } catch (error: any) {
        return { success: false, message: error.message || '获取批量回测状态失败' };
      }
    },
    interval: 3000,
    immediate: true,
    enabled: batchBacktesting && !!batchTaskId, // 声明式控制：当 batchBacktesting 为 true 且有 taskId 时自动启动
    onStatusUpdate: (status) => {
      setBatchStatus(status);
    },
    onCompleted: async () => {
      setBatchBacktesting(false);
      if (strategyCode) {
        localStorage.removeItem(`batch_backtest_task_${strategyCode}`);
      }
      await loadDetail();
      showToast('批量回测完成', 'success');
    },
    onFailed: async () => {
      setBatchBacktesting(false);
      if (strategyCode) {
        localStorage.removeItem(`batch_backtest_task_${strategyCode}`);
      }
      await loadDetail();
      showToast('批量回测失败', 'error');
    },
    onError: (message) => {
      setBatchBacktesting(false);
      setBatchStatus(null);
      setBatchTaskId(null);
      if (strategyCode) {
        localStorage.removeItem(`batch_backtest_task_${strategyCode}`);
      }
      showToast(message || '批量回测状态查询失败', 'error');
    },
    onCleanup: () => {
      if (strategyCode) {
        localStorage.removeItem(`batch_backtest_task_${strategyCode}`);
      }
    },
  });

  // 批量优化轮询 - 声明式控制，enabled 绑定到 optimizing 状态
  useTaskPolling<BatchOptimizeStatus>({
    pollFn: async () => {
      if (!optimizeTaskId) {
        return { success: false, message: '任务ID不存在' };
      }
      try {
        const data = await quantitativeStrategyService.getBatchOptimizeStatus(optimizeTaskId);
        return { success: true, data };
      } catch (error: any) {
        return { success: false, message: error.message || '获取批量优化状态失败' };
      }
    },
    interval: 3000,
    immediate: true,
    enabled: optimizing && !!optimizeTaskId, // 声明式控制：当 optimizing 为 true 且有 taskId 时自动启动
    onStatusUpdate: (status) => {
      setOptimizeStatus(status);
    },
    onCompleted: async () => {
      setOptimizing(false);
      if (strategyCode) {
        localStorage.removeItem(`batch_optimize_task_${strategyCode}`);
      }
      await loadDetail();
      showToast('批量优化完成', 'success');
    },
    onFailed: async (status) => {
      setOptimizing(false);
      if (strategyCode) {
        localStorage.removeItem(`batch_optimize_task_${strategyCode}`);
      }
      await loadDetail();
      showToast(`批量优化失败: ${status.error || '未知错误'}`, 'error');
    },
    onError: (message) => {
      setOptimizing(false);
      setOptimizeStatus(null);
      setOptimizeTaskId(null);
      if (strategyCode) {
        localStorage.removeItem(`batch_optimize_task_${strategyCode}`);
      }
      showToast(message || '批量优化状态查询失败', 'error');
    },
    onCleanup: () => {
      if (strategyCode) {
        localStorage.removeItem(`batch_optimize_task_${strategyCode}`);
      }
    },
  });

  // 从 sessionStorage 获取策略代码，并加载缓存的配置
  useEffect(() => {
    const code = sessionStorage.getItem('strategy_detail_code');
    if (code) {
      setStrategyCode(code);
      // 清除 sessionStorage，避免下次进入时误用
      sessionStorage.removeItem('strategy_detail_code');

      // 加载缓存的批量回测配置
      const backtestConfig = loadBatchBacktestConfig(code);
      setBatchTimeframe(backtestConfig.timeframe);
      setBatchStartDate(backtestConfig.startDate);
      setBatchEndDate(backtestConfig.endDate);

      // 加载缓存的批量优化配置（如果与回测配置不同，可以单独设置）
      // 这里为了简化，批量优化使用与批量回测相同的配置
    } else {
      setError('缺少策略代码参数');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载策略详情
  const loadDetail = async () => {
    if (!strategyCode) return;

    try {
      setLoading(true);
      setError(null);
      const data = await quantitativeStrategyService.getStrategyDetail(strategyCode);
      setDetail(data);
      // 如果有优化参数，默认选择第一个币种
      if (data?.strategy?.optimizedParamsMap) {
        const symbolKeys = Object.keys(data.strategy.optimizedParamsMap);
        if (symbolKeys.length > 0 && !selectedSymbolKey) {
          setSelectedSymbolKey(symbolKeys[0]);
        }
      }
    } catch (error: any) {
      console.error('获取策略详情失败:', error);
      setError(error.message || '获取策略详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (strategyCode) {
      loadDetail();
      // 检查是否有正在进行的任务
      checkAndResumeRunningTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyCode]);

  // 检查并恢复正在进行的任务（从 localStorage 读取）
  const checkAndResumeRunningTasks = () => {
    if (!strategyCode) return;

    // 从 localStorage 读取批量回测任务ID
    const batchBacktestTaskIdKey = `batch_backtest_task_${strategyCode}`;
    const cachedBatchBacktestTaskId = localStorage.getItem(batchBacktestTaskIdKey);
    if (cachedBatchBacktestTaskId) {
      setBatchTaskId(cachedBatchBacktestTaskId);
      setBatchStatus(null);
      setBatchBacktesting(true); // 设置状态后，enabled 会自动触发轮询
    }

    // 从 localStorage 读取批量优化任务ID
    const batchOptimizeTaskIdKey = `batch_optimize_task_${strategyCode}`;
    const cachedBatchOptimizeTaskId = localStorage.getItem(batchOptimizeTaskIdKey);
    if (cachedBatchOptimizeTaskId) {
      setOptimizeTaskId(cachedBatchOptimizeTaskId);
      setOptimizeStatus(null);
      setOptimizing(true); // 设置状态后，enabled 会自动触发轮询
    }
  };

  // 启动批量回测
  const startBatchBacktest = async () => {
    if (!strategyCode || batchBacktesting) return;

    // 验证时间范围
    if (!batchStartDate || !batchEndDate) {
      showToast('请选择开始日期和结束日期', 'error');
      return;
    }

    if (new Date(batchStartDate) >= new Date(batchEndDate)) {
      showToast('开始日期必须早于结束日期', 'error');
      return;
    }

    try {
      const { taskId } = await quantitativeStrategyService.startBatchBacktest(strategyCode, {
        timeframe: batchTimeframe,
        startTime: batchStartDate,
        endTime: batchEndDate,
        concurrency: 3,
      });

      // 设置任务ID和状态，enabled 会自动触发轮询
      setBatchTaskId(taskId);
      setBatchStatus(null);
      setBatchBacktesting(true);

      showToast('批量回测已启动', 'success');

      // 保存任务ID到 localStorage
      if (strategyCode) {
        localStorage.setItem(`batch_backtest_task_${strategyCode}`, taskId);

        // 保存批量回测配置到 localStorage
        localStorage.setItem(
          `batch_backtest_config_${strategyCode}`,
          JSON.stringify({
            timeframe: batchTimeframe,
            startDate: batchStartDate,
            endDate: batchEndDate,
          }),
        );
      }
    } catch (error: any) {
      console.error('启动批量回测失败:', error);
      showToast(error.message || '启动批量回测失败', 'error');
      setBatchBacktesting(false);
    }
  };

  // 启动批量优化
  const startBatchOptimize = async () => {
    if (!strategyCode || optimizing) return;

    // 验证时间范围
    if (!batchStartDate || !batchEndDate) {
      showToast('请选择开始日期和结束日期', 'error');
      return;
    }

    if (new Date(batchStartDate) >= new Date(batchEndDate)) {
      showToast('开始日期必须早于结束日期', 'error');
      return;
    }

    try {
      const { taskId } = await quantitativeStrategyService.startBatchOptimize(strategyCode, {
        timeframe: batchTimeframe,
        startTime: batchStartDate,
        endTime: batchEndDate,
        populationSize: 20,
        maxGenerations: 50,
      });

      // 设置任务ID和状态，enabled 会自动触发轮询
      setOptimizeTaskId(taskId);
      setOptimizeStatus(null);
      setOptimizing(true);

      showToast('批量优化已启动', 'success');

      // 保存任务ID到 localStorage
      if (strategyCode) {
        localStorage.setItem(`batch_optimize_task_${strategyCode}`, taskId);

        // 保存批量优化配置到 localStorage
        localStorage.setItem(
          `batch_optimize_config_${strategyCode}`,
          JSON.stringify({
            timeframe: batchTimeframe,
            startDate: batchStartDate,
            endDate: batchEndDate,
          }),
        );
      }
    } catch (error: any) {
      console.error('启动批量优化失败:', error);
      showToast(error.message || '启动批量优化失败', 'error');
      setOptimizing(false);
    }
  };

  // 清理轮询
  // 清理轮询：离开页面时停止所有轮询（由 useTaskPolling hook 自动处理）

  const formatPercent = (value: any) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 页面头部 */}
        <PageHeader
          title={detail?.strategy.name || '策略详情'}
          onBack={() => setCurrentPage('strategy-square')}
          rightContent={
            <button
              onClick={loadDetail}
              disabled={loading}
              className="group flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {loading && !detail ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-red-400 mb-4">{error}</div>
              <button
                onClick={loadDetail}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                重试
              </button>
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* 策略信息 */}
              <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                <h2 className="text-white font-semibold mb-2">策略信息</h2>
                <div className="text-white/60 text-sm space-y-1">
                  <div>分类: {detail.strategy.category}</div>
                  <div>描述: {detail.strategy.description}</div>
                </div>
              </div>

              {/* 批量回测按钮和状态 */}
              <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white font-semibold">批量回测</h2>
                  <button
                    onClick={startBatchBacktest}
                    disabled={batchBacktesting}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {batchBacktesting ? '回测中...' : '开始批量回测'}
                  </button>
                </div>

                <div className="space-y-3">
                  {/* 时间粒度选择 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">时间粒度</label>
                    <SelectDropdown
                      value={batchTimeframe}
                      onChange={(value) => setBatchTimeframe(value as string)}
                      options={COMMON_TIMEFRAMES}
                      placeholder="选择时间粒度"
                      disabled={batchBacktesting}
                      className="mb-0"
                    />
                  </div>

                  {/* 时间范围选择 */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">开始日期</label>
                      <input
                        type="date"
                        value={batchStartDate}
                        onChange={(e) => setBatchStartDate(e.target.value)}
                        disabled={batchBacktesting}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">结束日期</label>
                      <input
                        type="date"
                        value={batchEndDate}
                        onChange={(e) => setBatchEndDate(e.target.value)}
                        disabled={batchBacktesting}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {batchStatus && (
                  <div className="mt-3 text-sm text-white/60">
                    <div>
                      状态:{' '}
                      {batchStatus.status === 'running'
                        ? '运行中'
                        : batchStatus.status === 'completed'
                          ? '已完成'
                          : batchStatus.status === 'failed'
                            ? '失败'
                            : '等待中'}
                    </div>
                    <div>
                      进度: {batchStatus.status === 'completed' ? batchStatus.totalTasks : batchStatus.completedTasks} /{' '}
                      {batchStatus.totalTasks}
                    </div>
                    {batchStatus.currentTask && (
                      <div>
                        当前: {batchStatus.currentTask.symbol} ({batchStatus.currentTask.instType})
                      </div>
                    )}
                    {batchStatus.failedTasks > 0 && (
                      <div className="text-red-400 mt-2">
                        失败: {batchStatus.failedTasks} 个币种
                        <div className="text-xs mt-1">
                          {batchStatus.results
                            .filter((r) => r.status === 'failed')
                            .map((r) => `${r.symbol} (${r.instType}): ${r.error || '未知错误'}`)
                            .join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 策略参数对比 */}
              <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white font-semibold">策略参数</h2>
                  <button
                    onClick={startBatchOptimize}
                    disabled={optimizing}
                    className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {optimizing ? '优化中...' : '优化参数'}
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-white/80 text-sm font-semibold mb-2">默认参数</div>
                    <div className="bg-white/5 rounded-lg p-3 space-y-2">
                      {Object.entries(detail.strategy.defaultParams).map(([key, value]) => {
                        const description = detail.strategy.paramDescriptions?.[key] || key;
                        return (
                          <div key={key} className="text-xs">
                            <span className="text-white/80 font-semibold">{description}:</span>
                            <span className="text-white/60 ml-2">{String(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {Object.keys(detail.strategy.optimizedParamsMap).length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <div className="text-white/80 text-sm font-semibold whitespace-nowrap">优化参数</div>
                        <select
                          value={selectedSymbolKey}
                          onChange={(e) => setSelectedSymbolKey(e.target.value)}
                          className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40"
                        >
                          {Object.keys(detail.strategy.optimizedParamsMap).map((key) => (
                            <option key={key} value={key} className="bg-gray-800 text-white">
                              {key}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedSymbolKey && detail.strategy.optimizedParamsMap[selectedSymbolKey] ? (
                        <div className="mb-2">
                          <div className="text-white/60 text-xs mb-1">{selectedSymbolKey}</div>
                          <div className="bg-white/5 rounded-lg p-3 space-y-2">
                            {Object.entries(detail.strategy.optimizedParamsMap[selectedSymbolKey]).map(
                              ([key, value]) => {
                                const description = detail.strategy.paramDescriptions?.[key] || key;
                                return (
                                  <div key={key} className="text-xs">
                                    <span className="text-white/80 font-semibold">{description}:</span>
                                    <span className="text-white/60 ml-2">{String(value)}</span>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* 批量优化状态 */}
              {optimizeStatus && (
                <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                  <div className="text-white font-semibold mb-3">批量优化进度</div>
                  <div className="text-white/60 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <span>状态:</span>
                      <span
                        className={
                          optimizeStatus.status === 'running'
                            ? 'text-blue-400'
                            : optimizeStatus.status === 'completed'
                              ? 'text-green-400'
                              : optimizeStatus.status === 'failed'
                                ? 'text-red-400'
                                : 'text-white/60'
                        }
                      >
                        {optimizeStatus.status === 'running'
                          ? '运行中'
                          : optimizeStatus.status === 'completed'
                            ? '已完成'
                            : optimizeStatus.status === 'failed'
                              ? '失败'
                              : '等待中'}
                      </span>
                    </div>

                    {optimizeStatus.status === 'running' && optimizeStatus.maxGenerations > 0 && (
                      <>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span>进度:</span>
                            <span>
                              {optimizeStatus.currentGeneration} / {optimizeStatus.maxGenerations} (
                              {Math.round((optimizeStatus.currentGeneration / optimizeStatus.maxGenerations) * 100)}%)
                            </span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-300"
                              style={{
                                width: `${Math.min(100, (optimizeStatus.currentGeneration / optimizeStatus.maxGenerations) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span>最佳得分:</span>
                          <span className="text-white/80 font-semibold">
                            {optimizeStatus.bestScore !== null &&
                            optimizeStatus.bestScore !== undefined &&
                            optimizeStatus.bestScore !== -Infinity &&
                            !isNaN(optimizeStatus.bestScore)
                              ? `${optimizeStatus.bestScore >= 0 ? '+' : ''}${optimizeStatus.bestScore.toFixed(2)}%`
                              : '—'}
                          </span>
                        </div>

                        {optimizeStatus.bestParams && Object.keys(optimizeStatus.bestParams).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <div className="text-white/80 font-semibold mb-1 text-xs">当前最佳参数:</div>
                            <div className="bg-white/5 rounded-lg p-2 space-y-1">
                              {Object.entries(optimizeStatus.bestParams).map(([key, value]) => {
                                const description = detail?.strategy.paramDescriptions?.[key] || key;
                                return (
                                  <div key={key} className="text-xs flex justify-between">
                                    <span className="text-white/60">{description}:</span>
                                    <span className="text-white/80">{String(value)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {optimizeStatus.status === 'completed' && (
                      <>
                        {optimizeStatus.maxGenerations > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span>进度:</span>
                              <span>
                                {optimizeStatus.currentGeneration} / {optimizeStatus.maxGenerations} (100%)
                              </span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-300"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span>最佳得分:</span>
                          <span className="text-white/80 font-semibold">
                            {optimizeStatus.bestScore !== null &&
                            optimizeStatus.bestScore !== undefined &&
                            optimizeStatus.bestScore !== -Infinity &&
                            !isNaN(optimizeStatus.bestScore)
                              ? `${optimizeStatus.bestScore >= 0 ? '+' : ''}${optimizeStatus.bestScore.toFixed(2)}%`
                              : '—'}
                          </span>
                        </div>

                        {optimizeStatus.bestParams && Object.keys(optimizeStatus.bestParams).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <div className="text-white/80 font-semibold mb-1">优化后的参数:</div>
                            <div className="bg-white/5 rounded-lg p-3 space-y-1">
                              {Object.entries(optimizeStatus.bestParams).map(([key, value]) => {
                                const description = detail?.strategy.paramDescriptions?.[key] || key;
                                return (
                                  <div key={key} className="text-xs flex justify-between">
                                    <span className="text-white/80 font-semibold">{description}:</span>
                                    <span className="text-white/60">{String(value)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {optimizeStatus.status === 'failed' && optimizeStatus.error && (
                      <div className="text-red-400 text-xs">错误: {optimizeStatus.error}</div>
                    )}
                  </div>
                </div>
              )}

              {/* 平均回测结果 */}
              {detail.averageBacktest && (
                <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                  <h2 className="text-white font-semibold mb-3">平均回测结果</h2>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-white/60">收益率: </span>
                      <span
                        className={`font-semibold ${
                          detail.averageBacktest.totalReturnPct >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {formatPercent(detail.averageBacktest.totalReturnPct)}
                      </span>
                    </div>
                    <div>
                      <span className="text-white/60">胜率: </span>
                      <span className="text-white font-semibold">
                        {(() => {
                          const winRate = Number(detail.averageBacktest.winRate);
                          return (winRate > 1 ? winRate : winRate * 100).toFixed(1);
                        })()}
                        %
                      </span>
                    </div>
                    <div>
                      <span className="text-white/60">夏普比率: </span>
                      <span className="text-white font-semibold">
                        {Number(detail.averageBacktest.sharpeRatio).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-white/60">最大回撤: </span>
                      <span className="text-red-400 font-semibold">
                        {formatPercent(-Math.abs(Number(detail.averageBacktest.maxDrawdown)))}
                      </span>
                    </div>
                    <div>
                      <span className="text-white/60">总交易数: </span>
                      <span className="text-white font-semibold">{detail.averageBacktest.totalTrades}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 各币种回测结果列表 */}
              <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20">
                <h2 className="text-white font-semibold mb-3">
                  各币种回测结果 ({detail.validBacktests} / {detail.totalBacktests})
                </h2>
                {detail.backtests.length === 0 ? (
                  <div className="text-white/60 text-sm">暂无回测结果</div>
                ) : (
                  <div className="space-y-2">
                    {detail.backtests.map((backtest) => (
                      <div key={backtest.id} className="p-3 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white font-semibold">
                            {backtest.symbol} ({backtest.instType})
                          </div>
                          {backtest.totalReturnPct !== null && (
                            <div
                              className={`font-semibold ${
                                backtest.totalReturnPct >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {formatPercent(backtest.totalReturnPct)}
                            </div>
                          )}
                        </div>
                        {backtest.totalReturnPct !== null &&
                        backtest.winRate !== null &&
                        backtest.sharpeRatio !== null &&
                        backtest.maxDrawdown !== null ? (
                          <div className="grid grid-cols-2 gap-2 text-xs text-white/60">
                            <div>
                              <span>胜率: </span>
                              <span className="text-white">
                                {(() => {
                                  const winRate = Number(backtest.winRate);
                                  return (winRate > 1 ? winRate : winRate * 100).toFixed(1);
                                })()}
                                %
                              </span>
                            </div>
                            <div>
                              <span>夏普: </span>
                              <span className="text-white">{Number(backtest.sharpeRatio).toFixed(2)}</span>
                            </div>
                            <div>
                              <span>回撤: </span>
                              <span className="text-red-400">
                                {formatPercent(-Math.abs(Number(backtest.maxDrawdown)))}
                              </span>
                            </div>
                            <div>
                              <span>交易数: </span>
                              <span className="text-white">{backtest.totalTrades || 0}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-red-400 text-xs">回测数据不完整</div>
                        )}
                        <div className="text-white/40 text-xs mt-2">{formatDate(backtest.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </GradientBackground>
  );
};

export default StrategyDetail;
